import { centerDemoField } from 'components/cabinet/demo/center-field';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import sortBy from "lodash/sortBy";
import moment from "moment";

import { Add as AddIcon } from '@vedmarket/ui-kit';
import { TableView as TableViewIcon } from '@vedmarket/ui-kit';
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import {
  useDeclarationSummaryByOrderIdQuery,
  useInvoicesByOrderIdQuery,
  useLazyProductsSearchByInvoiceIdQuery,
  useProductsByInvoiceIdQuery,
  useRemoveProductMutation,
  useSummary,
  useGetOrder,
  useProductsList,
  useCreateProductMutation,
} from "../../../../../../../../../../../api/calc";

import ModalAction from "../../../../../../../../../../common/modalAction";
import ModalConfirm from "../../../../../../../../../../common/modalConfirm";
import {isDemoPage, useGetCalcIdFromRoute} from "../../../../../../../../../../../utils/hooks";
import {useDemoProductsTable} from 'components/cabinet/demo/steps/packaging/use-packaging';
import {formatHSCode, isCalculationViewModeError, numberWithSpaces} from "../../../../../../../../../../../utils/helpers";
import {COLORS} from "../../../../../../../../../../../config/constants";
import {useLazyGetTnvedQuery} from "../../../../../../../../../../../api/tnved";
import ModalCode from "../../../../../../../customs/components/modal_code";

import ProductsTable from "../products_table";
import AddForm from "./components/add_form";
import { ModalBig } from "@vedmarket/ui-kit";

import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../../../../../../../calculation-read-only';

import ProductHeading from './components/product-heading';
import SavedProduct from './components/saved-product';

import './products.scss';

type Props = {
    openByDefault?: boolean
    invoice?: InvoiceParamsOutput
    productCount: number
    testDriveScenarioSlug?: string
    handleRefetchRequests(options?: { skipOrderSummary?: boolean }): void
}

const Products = ({openByDefault, invoice, productCount, testDriveScenarioSlug, handleRefetchRequests} : Props) => {

  const { t } = useTranslation()

  const isDemoMode = isDemoPage()
  const readOnly = useCalculationReadOnly()

  const { data: products } = useProductsByInvoiceIdQuery(invoice?.id || 0, {skip: !invoice?.id})

  const [ showAddForm, setShowAddFrom ] = useState<boolean | undefined>(false);

  const [ openEditModal, setOpenEditModal ] = useState(false);
  const [ shouldOpenEditConfirmModal, setShouldOpenEditConfirmModal ] = useState(false);
  const [ editFormHasChanged, setEditFormHasChanged ] = useState(false);
  const [ editFormSaveRequestToken, setEditFormSaveRequestToken ] = useState(0);

  const [ openProductsModal, setOpenProductsModal ] = useState(false);

  const demoProductsTable = useDemoProductsTable(() => setOpenProductsModal(true))

  const [ currentProductId, setCurrentProductId ] = useState<number>();

  const [ shouldOpenRemoveModal, setShouldOpenRemoveModal ] = useState(false)

  const [ shouldRefetchCustoms, setShouldRefetchCustoms ] = useState(true);

  const [ openModalCode, setOpenModalCode ] = useState(false);

  const [ tnvedInfo, setTnvedInfo ] = useState<TnvedInfo>();

  const [ copiedProduct, setCopiedProduct ] = useState<ProductParamsOutput>();
  const [ selectedProductPhoto, setSelectedProductPhoto ] = useState<ProductPhoto>();

  const [ tnvedRequest ] = useLazyGetTnvedQuery()

  const [ productsListRequest ] = useLazyProductsSearchByInvoiceIdQuery()

  const handleClickCodeLink = (code: string) => {
    tnvedRequest(code)?.then((response) => {
      setTnvedInfo(response?.data);
      setOpenModalCode(true);
    });
  }

  const productsListParams = useProductsList()?.params;

  const [ removeProduct ] = useRemoveProductMutation()
  const [ createProduct ] = useCreateProductMutation()

  const orderId = useGetCalcIdFromRoute()

  const demoProduct = isDemoMode ? products?.[0] : undefined

  const summaryOrder = useSummary();

  const order = useGetOrder();

  const isPackagingCompleted = Boolean(summaryOrder?.delivery_type);

  const {data: invoices, isSuccess} = useInvoicesByOrderIdQuery(orderId, {skip: !orderId})
  const {refetch: refetchInvoices} = useInvoicesByOrderIdQuery(orderId, {skip: !orderId})
  const {refetch: refetchSummaryDeclaration} = useDeclarationSummaryByOrderIdQuery(orderId, {skip: !shouldRefetchCustoms || !orderId})
  const {refetch: refetchProductsByInvoiceId, data: productsByInvoiceId} = useProductsByInvoiceIdQuery(invoice?.id || 0, {skip: !invoice?.id})

  const addProductRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openByDefault) {
      setShowAddFrom(!invoices);
    }
  }, [ isSuccess ])

  useEffect(() => {
    if (isDemoPage()) {
      if (demoProduct) {
        setShowAddFrom(Boolean(invoice?.id) && !copiedProduct);
      }

      return;
    }

    setShowAddFrom(!products?.length && Boolean(invoice?.id) && !copiedProduct);
  }, [ copiedProduct, demoProduct, invoice, products ])

  useEffect(() => {
    setCopiedProduct(undefined);
  }, [ invoice?.id ])

  const getCompletedInfoProduct = (product: ProductParamsOutput) =>
    [
      {
        label: t('common__text__codeOfHS'),
        value: <div>
          <a
            className='products__item__link'
            onClick={() => handleClickCodeLink(product.code)}>{formatHSCode(product.code)}</a>
          <ModalCode
            isOpen={openModalCode}
            onClose={() => setOpenModalCode(false)}
            code={product.code}
            tnvedInfo={tnvedInfo}
          />
        </div>
      },
      {
        label: t('common__text__totalQuantity'),
        value: `${product.pieces} ${t('units__text__items')}`
      },
      {
        label: t('common__text__pricePerPiece'),
        value: `${numberWithSpaces(product.unit_price)} USD`
      },
      {
        label: t('common__text__totalCost'),
        value: `${numberWithSpaces(product.price_total)} USD`
      },
      ...(product.places_total
        ? [
          {
            label: t('common__text__numberOfPlaces'),
            value: `${product.places_total} ${t('units__text__items')}`
          },
        ]
        : []),
      ...(product.pieces_in_cartoon
        ? [
          {
            label: t('common__text__quantityPerPackage'),
            value: `${product.pieces_in_cartoon} ${t('units__text__items')}`
          },
        ]
        : []),
      ...(product.length
        ? [
          {
            label: t("common__text__lengthWidthHeight"),
            value: `${numberWithSpaces(product.length)} x ${numberWithSpaces(product.width)} x ${numberWithSpaces(product.height)} ${t("units__text__centimetre")}`,
          },
        ]
        : []),
      ...(product.volume_cartoon
        ? [
          {
            label: t('common__text__packageVolume'),
            value: <>
              {`${numberWithSpaces(product.volume_cartoon)} ${t('units__text__meter')}`}
              <sup>3</sup>
            </>
          },
        ]
        : []),
      ...(product.net_weight_cartoon
        ? [
          {
            label: t('common__text__packageWeight'),
            value: <>
              {`${numberWithSpaces(product.net_weight_cartoon)} ${t('units__text__kg')}`} / {`${numberWithSpaces(product.gross_weight_cartoon)} ${t('units__text__kg')}`}
            </>
          },
        ]
        : []),
      ...(product.volume_total
        ? [
          {
            label: t('common__text__totalVolume'),
            value: <>
              {`${numberWithSpaces(product.volume_total)} ${t('units__text__meter')}`}
              <sup>3</sup>
            </>
          },
        ]
        : []),
      ...(product.net_weight_total
        ? [
          {
            label: t('common__text__netGrossWeight'),
            value: <>
              {`${numberWithSpaces(product.net_weight_total)} ${t('units__text__kg')}`} / {`${numberWithSpaces(product.gross_weight_total)} ${t('units__text__kg')}`}
            </>
          },
        ]
        : []),
      ...(product.volume_manual
        ? [
          {
            label: t("common__text__totalVolume"),
            value: <>
              {`${numberWithSpaces(product.volume_manual)} ${t('units__text__meter')}`}
              <sup>3</sup>
            </>,
          },
        ]
        : []),
      ...(product.net_weight_manual
        ? [
          {
            label: t("common__text__totalNetWeight"),
            value: <>
              {`${numberWithSpaces(product.net_weight_manual)} ${t('units__text__kg')}`}
              <sup>3</sup>
            </>,
          },
        ]
        : []),
      ...(product.gross_weight_manual
        ? [
          {
            label: t("common__text__totalGrossWeight"),
            value: <>
              {`${numberWithSpaces(product.gross_weight_manual)} ${t('units__text__kg')}`}
              <sup>3</sup>
            </>,
          },
        ]
        : []),
      ...(product.pallete_cnt
        ? [
          {
            label: t("calc__text__packaging__numberOfPalletes"),
            value: product.pallete_cnt,
          },
        ]
        : []),
      ...(product.pallete_height
        ? [
          {
            label: t("calc__text__packaging__heightOfThePallet"),
            value: numberWithSpaces(product.pallete_height),
          },
        ]
        : []),
      ...(product.pallete_type
        ? [
          {
            label: t("calc__text__packaging__palletType"),
            value: product?.pallete_type?.toUpperCase(),
          },
        ]
        : []),
      ...(product.pallete_weight
        ? [
          {
            label: t("calc__text__packaging__weightOfThePallet"),
            value: numberWithSpaces(product.pallete_weight),
          },
        ]
        : []),
    ]

  const onEdit = (id: number) => {
    setCurrentProductId(id);
    setEditFormHasChanged(false);
    setEditFormSaveRequestToken(0);
    setOpenEditModal(true)
  }

  const removeItem = (productId?: number | string) => {
    if (productId && invoice?.id) {
      removeProduct({
        invoiceId: invoice?.id,
        productId: Number(productId)
      })
        .unwrap()
        .then(async () => {
          setShouldOpenRemoveModal(false)
          setCopiedProduct((currentProduct) => currentProduct?.id === Number(productId) ? undefined : currentProduct)
          refetchProductsByInvoiceId()
          refetchInvoices()

          if (isPackagingCompleted) {
            handleRefetchRequests();
          } else {
            refetchSummaryDeclaration()
          }

          if (openProductsModal) {
            productsListRequest(productsListParams);
          }
        })
        .catch((error) => {
          if (!isCalculationViewModeError(error)) {
            throw error;
          }
        })

    }
  }

  const onRemove = (id: number) => {
    setCurrentProductId(id)
    setShouldOpenRemoveModal(true)
  }

  const onCopy = async (product: any) => {
    const createdProduct = await createProduct({
      invoiceId: invoice?.id,
      ...product
    })
      .unwrap()
      .catch(() => null);

    if (!createdProduct) {
      return;
    }

    setShowAddFrom(false);
    setCopiedProduct(createdProduct);
    refetchInvoices()
    refetchProductsByInvoiceId();
    setShouldRefetchCustoms(true);

    requestAnimationFrame(() => {
      const productElement = addProductRef.current;
      if (productElement && !centerDemoField(productElement)) {
        productElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    if (isPackagingCompleted) {
      handleRefetchRequests({ skipOrderSummary: true });
    }
  }

  const handleCloseAddForm = (shouldRefetchRequests = true) => {
    setShowAddFrom(false)
    setOpenEditModal(false)
    setShouldOpenEditConfirmModal(false)
    setEditFormHasChanged(false)

    if (isDemoPage()) {
      return
    }

    refetchProductsByInvoiceId()

    if (openProductsModal) {
      productsListRequest(productsListParams);
    }

    shouldRefetchRequests && handleRefetchRequests();

  }

  const handleSuccessAddedProduct = () => {

    if (isDemoPage()) {
      return;
    }

    refetchInvoices()
    refetchProductsByInvoiceId();
    setShouldRefetchCustoms(true);

    setShowAddFrom(false);

    requestAnimationFrame(() => {
      const productElement = addProductRef.current;
      if (productElement && !centerDemoField(productElement)) {
        productElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    if (isPackagingCompleted) {
      handleRefetchRequests({ skipOrderSummary: true });
    }

  }

  let productsWithCopiedProduct = products;

  if (copiedProduct) {
    const copiedProductExists = products?.some((product) => product.id === copiedProduct.id);

    if (!copiedProductExists) {
      productsWithCopiedProduct = [ ...(products || []), copiedProduct ];
    }
  }

  const sortedProducts = sortBy(productsWithCopiedProduct, 'id');

  const findProductNameById = (productId?: number) => {

    if (!productsWithCopiedProduct || !productId) {
      return;
    }

    // @ts-ignore
    return productsWithCopiedProduct?.find((product) => product.id === Number(productId))?.name;
  }

  const handleRemove = () => {
    setOpenEditModal(false);
    setShouldOpenRemoveModal(true);
  }

  const handleCloseEditModal = () => {
    if (editFormHasChanged) {
      setShouldOpenEditConfirmModal(true);
      return;
    }

    setOpenEditModal(false);
  }

  const handleCloseEditConfirmModal = () => {
    setShouldOpenEditConfirmModal(false);
  }

  const handleCloseEditWithoutSaving = () => {
    setShouldOpenEditConfirmModal(false);
    setOpenEditModal(false);
    setEditFormHasChanged(false);
  }

  const handleSaveEditAndClose = () => {
    setShouldOpenEditConfirmModal(false);
    setEditFormSaveRequestToken((token) => token + 1);
  }

  const handleRemoveCopiedProduct = () => {
    if (!copiedProduct?.id) {
      setShowAddFrom(false);
      return;
    }

    setCurrentProductId(copiedProduct.id);
    setShouldOpenRemoveModal(true);
  }

  const handleSuccessCopiedProduct = () => {
    setCopiedProduct(undefined);
    handleSuccessAddedProduct();
  }

  const handleOpenPhotoModal = (photo?: ProductPhoto) => {
    setSelectedProductPhoto(photo);
  }

  const shouldShowAddButton = Boolean(invoice?.id) && !showAddForm && !copiedProduct;

  // @ts-ignore
  const titleProductTable = [
    t('calc__text__packaging__tableOfProducts'),
    `${order?.data?.order_name}. ${t('common__text__invoice')} № ${invoice?.invoice_num} ${t('common__text__from')} ${moment(invoice?.invoice_date).format('Do MMMM YYYY')}`,
  ].join('. ');

  return (
    <div className='products'>
      {(productCount > 0 || showAddForm || Boolean(copiedProduct)) && (
        <ProductHeading count={productCount} />
      )}
      {!isDemoPage() && sortedProducts?.map((product, index) => (
        copiedProduct?.id === product.id ? (
          <div className='products__add-form' key={product.id}>
            <AddForm
              handleRemove={handleRemoveCopiedProduct}
              handleSuccess={handleSuccessCopiedProduct}
              hideClearButton
              id={product.id}
              invoice={invoice}
              product={copiedProduct}
              count={index + 1}
            />
          </div>
        ) : (
          <div className='products__item-completed' key={product.id}>
            <SavedProduct
              product={product}
              position={index + 1}
              info={getCompletedInfoProduct(product)}
              actions={{
                onEdit: () => onEdit(product.id),
                onRemove: () => onRemove(product.id),
                onCopy: () => onCopy(product),
                onOpenPhoto: handleOpenPhotoModal,
              }}
            />
          </div>
        )
      ))}
      {showAddForm && (<div className='products__add-form'>
        <AddForm
          handleRemove={() => {
            setCopiedProduct(undefined);
            setShowAddFrom(false)
          }}
          invoice={invoice}
          count={productCount + 1}
          id={demoProduct?.id}
          product={demoProduct}
          testDriveScenarioSlug={testDriveScenarioSlug}
          handleSuccess={handleSuccessAddedProduct}
        />
      </div>)}
      {invoice?.id && <Tooltip
        arrow
        {...demoProductsTable.tooltipProps}
        title={demoProductsTable.shouldShowTooltip ? t('calc__text__demoProductsTableUnavailable') : ''}
      >
        <IconButton
          {...demoProductsTable.buttonProps}
          sx={{
            padding: 0,
            '&:hover': {background: 'transparent'},
            'svg': {fill: COLORS.BLUE}
          }}
        >
          <TableViewIcon style={{marginRight: '8px', color: COLORS.BLUE}}/>
          <Typography variant='body1' sx={{color: COLORS.BLUE}}>
            {t('calc__text__packaging__tableOfProducts')}
          </Typography>
        </IconButton>
      </Tooltip>}
      {shouldShowAddButton && (
        <div className='products__add-button' ref={addProductRef}>
          <Button
            variant='outlined'
            fullWidth
            className={cx({ 'read-only-action-button': readOnly })}
            disabled={readOnly}
            onClick={readOnly ? undefined : () => {
              setCopiedProduct(undefined);
              setShowAddFrom(true);
            }}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
            startIcon={<AddIcon />}
            endIcon={readOnlyButtonEndIcon(readOnly)}
          >
            {t('actions__text__addItem')}
          </Button>
        </div>
      )}
      {openEditModal && <ModalBig
        isOpen
        onClose={handleCloseEditModal}
        title={t('actions__text__editItem')}
      >
        <AddForm
          invoice={invoice}
          handleSuccess={handleCloseAddForm}
          id={currentProductId}
          count={sortedProducts.findIndex(product => product.id === currentProductId) + 1}
          handleRemove={handleRemove}
          insideModal
          testDriveScenarioSlug={testDriveScenarioSlug}
          onDirtyChange={setEditFormHasChanged}
          saveRequestToken={editFormSaveRequestToken}
        />
      </ModalBig>}
      {shouldOpenEditConfirmModal && <ModalConfirm
        isOpen={shouldOpenEditConfirmModal}
        title={t('actions__text__editItem')}
        description={t('calc__text__packaging__exitEditItemWithoutSaving')}
        handleClose={handleCloseEditConfirmModal}
        handleCloseWithoutSaving={handleCloseEditWithoutSaving}
        handleSave={handleSaveEditAndClose}
      />}
      {openProductsModal && invoice?.id && <ModalBig
        isOpen
        onClose={() => setOpenProductsModal(false)}
        title={titleProductTable}
        width={1344}
        noPadding
      >
        <ProductsTable
          invoiceId={invoice?.id}
          onEdit={onEdit}
          onRemove={onRemove}
          title={titleProductTable}
        />
      </ModalBig>}
      {selectedProductPhoto?.url && <ModalBig
        isOpen
        onClose={() => setSelectedProductPhoto(undefined)}
        title=''
        customStyles={{
          background: COLORS.BLACK,
          boxShadow: 'none',
        }}
        noPadding
      >
        <img
          alt={selectedProductPhoto.title}
          className='products__manufacturer-photo-full'
          src={selectedProductPhoto.url}
        />
      </ModalBig>}
      {shouldOpenRemoveModal && <ModalAction
        isOpen={shouldOpenRemoveModal}
        onContinue={removeItem}
        index={currentProductId}
        onClose={() => setShouldOpenRemoveModal(false)}
        title={t('common__text__deleteItem')}
        description={<>
          {t('actions__message__removeConfirm')}&nbsp;
          <span className='text_bold'>{`«${findProductNameById(currentProductId)}»?`}</span>
        </>}
        buttonYesText={t('actions__text__yesDelete')}
      />}
    </div>
  )
}

export default Products
